/* globals Stripe */

import {Component} from 'panel';

import {handleJsonRequest} from 'common/report/queries/utils';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {COUNTRY_CODES} from 'common/util/country-codes';
import * as colors from 'common/util/colors';

import {template} from './template';
import {getPostalCodeError} from './util';
import './index.styl';

type StripeCCFormState = {
  errors: any;
  selectedCountryCode: string;
  selectedPostalCode: string;
  clientSecret: string;
  reviewBtnLoading: boolean;
};

const POSTAL_CODE_COUNTRIES = new Set([
  `US`, // United States
  `CA`, // Canada
  `GB`, // United Kingdom
]);

const SANCTIONED_COUNTRIES = new Set([
  `CU`, // Cuba
  `IR`, // Iran
  `KP`, // North Korea
  `SD`, // Sudan
  `SY`, // Syria
]);

const NON_COUNTRY_ENTITIES = new Set([
  ``, // empty
  `A1`, // Anonymous Proxy
  `A2`, // Satellite Provider
  `AQ`, // Antarctica
  `country_iso_code`, // uhhhhh
]);

export class StripeCCForm extends Component<StripeCCFormState> {
  private stripeElems: {[elem: string]: any} = {};
  private stripe;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'is-modal': `boolean`,
      'account-id': {type: `number`, description: `Account ID to update`},
      'editing-card-info': `boolean`,
      'show-not-charged-today': `boolean`,
      'review-btn-loading': `boolean`,
      'billing-card': `json`,
      'mp-2023-style': {
        type: `boolean`,
        description: `flag to check whether page should apply new 2023 stying`,
      },
    };
  }

  get config() {
    return {
      template,
      defaultState: {
        errors: {},
        selectedCountryCode: ``,
        selectedPostalCode: ``,
        clientSecret: ``,
        reviewBtnLoading: false,
      },
      helpers: {
        POSTAL_CODE_COUNTRIES,
        getStripeElems: () => this.stripeElems,
        insertStripeElements: (stripeKey?) => {
          // see https://stripe.com/docs/stripe-js/reference#the-elements-object
          // https://github.com/stripe/elements-examples/
          // @ts-ignore
          if (!this.stripe) this.stripe = Stripe(stripeKey);
          const showMP2023Branding = this.attr(`mp-2023-style`);
          const elements = this.stripe.elements({
            fonts: [
              {
                family: `Apercu Pro`,
                src: `url(https://cdn.mxpnl.com/static/marketing/fonts/apercu/web/5.007/apercu-regular-pro.woff2)`, // taken from iron/common/assets/fonts/apercu.styl
                weight: `500`,
              },
              {
                family: `Garnett`,
                src: `url("https://cdn.mxpnl.com/static/marketing/fonts/garnett/garnett-medium.woff2")`, // taken from media/css/billing/pricing4/mayven.css
                weight: `400`,
              },
            ],
          });
          const style = {
            base: {
              color: colors.gray5,
              fontSize: `14px`,
              fontSmoothing: `antialiased`,
              fontFamily:
                `${showMP2023Branding && `'Garnett',`}` +
                `'Apercu Pro', 'Helvetica', 'Tahoma', 'Geneva', 'Arial', sans-serif`,
              fontWeight: `400`,
              letterSpacing: `0.02em`,

              '::placeholder': {
                color: showMP2023Branding ? `#626266` : colors.mpColors[`$gray3`],
                fontWeight: 400,
              },
            },
          };

          const classes = {
            focus: `focus`,
            empty: `empty`,
            complete: `complete`,
            invalid: `invalid`,
          };

          const allStripeElems = [`cardNumber`, `cardExpiry`, `cardCvc`];

          allStripeElems.forEach((elem) => {
            const stripeEl = elements.create(elem, {
              style,
              classes,
              ...(elem === `cardCvc` && {placeholder: `3 or 4 digits`}),
            });
            stripeEl.mount(`#${elem}`);
            stripeEl.on(`ready`, () => {
              // The Stripe Element docs make it unclear whether or not the element needs to
              // be ready before adding a change event listener, so we'll do it to be safe
              stripeEl.on(`change`, (ev) => {
                const errors = {...this.state.errors};
                errors[ev.elementType] = (ev.error && ev.error.message) || ``;
                this.update({errors});
              });
            });

            if (elem === `cardNumber`) {
              // default auto-focus on card input. stripe input takes a while to load
              setTimeout(() => stripeEl.focus(), 500);
            }

            this.stripeElems[elem] = stripeEl;
          });
        },
        getSelectedCountry: (): string => {
          const countryCode = this.state.selectedCountryCode;
          return countryCode ? COUNTRY_CODES[countryCode] : `Select Country`;
        },
        getCountriesItems: () => {
          return [
            {
              items: Object.entries(COUNTRY_CODES)
                .filter(([countryCode]) => !SANCTIONED_COUNTRIES.has(countryCode))
                .filter(([countryCode]) => !NON_COUNTRY_ENTITIES.has(countryCode))
                .map(([countryCode, label]) => ({
                  label,
                  countryCode,
                }))
                // @ts-ignore - only fails in test mode. WTF! `Property 'localeCompare' does not exist on type '{}'`
                .sort((a, b) => a.label.localeCompare(b.label)),
            },
          ];
        },
        handleCountrySelectChange: (ev) => {
          const countryCode = ev.detail.item.countryCode;
          this.update({selectedCountryCode: countryCode});
          this.dispatchEvent(new CustomEvent(`change-selected-payment-info`, {detail: {countryCode}}));
        },
        handlePostalCodeChange: (ev) => {
          const selectedPostalCode = ev.target.value.trim();

          const {selectedCountryCode, errors} = this.state;
          const errorMessage = selectedPostalCode && getPostalCodeError(selectedPostalCode, selectedCountryCode);
          this.update({errors: {...errors, postalCode: errorMessage}, selectedPostalCode});
          if (!errorMessage) {
            this.dispatchEvent(
              new CustomEvent(`change-selected-payment-info`, {detail: {postalCode: selectedPostalCode}}),
            );
          }
        },
        handlePostalCodeInput: (ev) => {
          const selectedPostalCode = ev.target.value.trim();
          const {selectedCountryCode, errors} = this.state;
          const errorMessage = selectedPostalCode && getPostalCodeError(selectedPostalCode, selectedCountryCode);
          if (!errorMessage) {
            this.update({errors: {...errors, postalCode: errorMessage}, selectedPostalCode});
          }
        },
        isValidCountryAndPostalCode: (): boolean => {
          const {selectedCountryCode, selectedPostalCode} = this.state;
          if (POSTAL_CODE_COUNTRIES.has(selectedCountryCode)) {
            return selectedPostalCode && !getPostalCodeError(selectedPostalCode, selectedCountryCode);
          }
          return Boolean(selectedCountryCode);
        },
        disableReviewButton: () => {
          return (
            this.state.reviewBtnLoading ||
            (!this.helpers.isValidForm() && this.attr(`is-modal`)
              ? this.attr(`editing-card-info`) || Boolean(!this.attr(`billing-card`))
              : !this.helpers.isValidForm())
          );
        },
        isValidForm: () => {
          return (
            this.helpers.isValidCountryAndPostalCode() &&
            !Object.values(this.stripeElems).some((el) => el._invalid || el._empty) &&
            !Object.values(this.state.errors).some((field) => field)
          );
        },
        waitAndInsertStripe: () => {
          new MutationObserver((_mutations, observer) => {
            const el = document.getElementById(`cardNumber`);
            if (el) {
              observer.disconnect();
              this.helpers.insertStripeElements();
            }
          }).observe(document.getElementById(`payment-info`), {
            subtree: true,
            childList: true,
          });
        },
      },
    };
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    if (attr === `review-btn-loading`) {
      this.update({reviewBtnLoading: this.attr(`review-btn-loading`)});
    }
  }
  async connectedCallback() {
    super.connectedCallback();

    // @ts-ignore
    if (globalThis.stripe) {
      // stripe already loaded (IE: PB2)
      // @ts-ignore
      this.stripe = globalThis.stripe;
      this.helpers.insertStripeElements();
    } else {
      // stripe not yet loaded (IE: org settings)
      const stripeKey = (await this.fetchStripeKey()).results[`stripe_publishable_key`];
      const clientSecret = await this.fetchClientSecret();
      this.update({clientSecret});
      this.addStripeScript(stripeKey);
    }
  }

  // Trying to keep separation of concerns back to parent component, but requires some gimmicky looking code
  // Passing back stripe elems, card setup result, and postal/country codes
  passSubmitPB2() {
    const {selectedCountryCode, selectedPostalCode} = this.state;
    this.dispatchEvent(
      new CustomEvent(`submit-pb2`, {
        detail: {
          cardElement: this.helpers.getStripeElems(),
          selectedPostalCode,
          selectedCountryCode,
        },
      }),
    );
  }

  // request logic for org settings need to be moved into this component to properly update loading state
  async makeRequest() {
    this.update({reviewBtnLoading: true});
    const cardElement = this.helpers.getStripeElems();
    const cardSetupResult = await this.stripe.confirmCardSetup(this.state.clientSecret, {
      payment_method: {card: cardElement.cardNumber},
    });
    if (cardSetupResult.error) {
      this.update({reviewBtnLoading: false});
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const {code, decline_code, doc_url, message, payment_method, setup_intent} = cardSetupResult.error;
      this.dispatchEvent(
        new CustomEvent(`error-stripe-update`, {
          detail: {
            message: `Card cannot be authorized. Contact your card issuer or try a different card.`,
            reason: {
              code,
              decline_code,
              doc_url,
              message,
              payment_method_id: payment_method.id,
              setup_intent_id: setup_intent.id,
            },
          },
        }),
      );
    } else {
      try {
        const response = await handleJsonRequest(
          fetch(`/api/app/billing/attach_payment_method/`, {
            method: `POST`,
            body: JSON.stringify({
              account_id: this.attr(`account-id`),
              payment_method_id: cardSetupResult.setupIntent.payment_method,
              country: this.helpers.getSelectedCountry(),
              postal_code: this.state.selectedPostalCode,
            }),
          }),
        );

        if (response.status === `ok`) {
          this.dispatchEvent(
            new CustomEvent(`success-stripe-update`, {
              detail: {success: true},
            }),
          );
          this.update({reviewBtnLoading: false});
        } else {
          throw new Error(response);
        }
      } catch (err) {
        this.update({reviewBtnLoading: false});
        this.dispatchEvent(
          new CustomEvent(`error-stripe-update`, {
            detail: {
              message: `There was a problem processing your payment information.`,
              reason: {
                message: `attach_payment_method request failed`,
                payment_method_id: cardSetupResult.setupIntent.payment_method,
              },
            },
          }),
        );
      }
    }
  }

  async fetchStripeKey() {
    try {
      const response = await handleJsonRequest(
        fetch(`/api/app/billing/get-stripe-publishable-key`, {
          method: `GET`,
        }),
      );
      return response;
    } catch (err) {
      this.dispatchEvent(
        new CustomEvent(`error-stripe-update`, {
          detail: {
            message: `Error communicating with Stripe.`,
            reason: {
              message: `get-stripe-publishable-key request failed`,
            },
          },
        }),
      );
      this.dispatchEvent(new CustomEvent(`cancel-modal`, {}));
    }
  }

  async fetchClientSecret() {
    try {
      const response = await handleJsonRequest(
        fetch(`/api/app/billing/create_setup_intent/`, {
          method: `POST`,
          body: JSON.stringify({account_id: this.attr(`account-id`)}),
        }),
      );
      return response.results.client_secret;
    } catch (err) {
      this.dispatchEvent(
        new CustomEvent(`error-stripe-update`, {
          detail: {
            message: `Error trying to attach your card details to your account for payment.`,
            reason: {
              message: `fetch client secret (create_setup_intent) request failed`,
            },
          },
        }),
      );
      this.dispatchEvent(new CustomEvent(`cancel-modal`, {}));
    }
  }

  addStripeScript(stripeKey) {
    const scriptElement = document.createElement(`script`);
    scriptElement.src = `https://js.stripe.com/v3/`;
    scriptElement.addEventListener(`load`, this.helpers.insertStripeElements.bind(this, stripeKey), false);
    const script = document.getElementsByTagName(`script`)[0];
    script?.parentNode.insertBefore(scriptElement, script);
  }
}

export const StripeCCFormEl = defineAndMakeJsxFactory(`mp-stripe-cc-form`, StripeCCForm);
export const StripeCCFormReact = panel2react(`mp-stripe-cc-form`, StripeCCForm);
